import { queryOptions, useQuery } from '@tanstack/react-query';

import { QueryRefetchInterval } from '@cast/shared-types';
import { Node, NodeResponse } from '@cast/types';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';
import { enhanceNode } from 'utils/api-utils';

const EXTERNAL_CLUSTER_NODE_QUERY_KEY = 'external-cluster-node';

export const getNodeQueryKey = (params: {
  clusterId: string;
  nodeId: string;
}) =>
  [EXTERNAL_CLUSTER_NODE_QUERY_KEY, params.clusterId, params.nodeId] as const;

type NodeQueryParams = {
  clusterId?: string;
  nodeId?: string;
  refetchInterval?: QueryRefetchInterval<Node>;
  enabled?: boolean;
};

const getNodeQueryOptions = ({
  clusterId,
  nodeId,
  refetchInterval,
  enabled = true,
}: NodeQueryParams) =>
  queryOptions<Node>({
    queryKey: getNodeQueryKey({ clusterId: clusterId!, nodeId: nodeId! }),
    queryFn: async () => {
      const { data } = await apiClient.clusters.getNode({
        clusterId: clusterId!,
        nodeId: nodeId!,
      });
      return enhanceNode(data as NodeResponse);
    },
    enabled: makeIsEnabled(!!nodeId && !!clusterId, enabled),
    refetchInterval,
  });

export const useNodeQuery = ({
  clusterId,
  nodeId,
  refetchInterval,
  enabled = true,
}: NodeQueryParams) => {
  const { isLoading, data, error, refetch } = useQuery(
    getNodeQueryOptions({
      clusterId,
      nodeId,
      refetchInterval,
      enabled,
    })
  );

  return {
    isLoading,
    node: data,
    error,
    refetch,
  };
};
